import { Button, Divider } from "@blueprintjs/core";
import { Tooltip2 } from "@blueprintjs/popover2";
import classNames from "classnames";
import React from "react";
import { SketchPicker } from "react-color";

import { usePlayContext } from "../../pages";
import { getBallLabelFromSport, getSetLabelFromSport } from "../../utils";
import { FastAddContextMenu } from "./components";
import styles from "./draw-toolbar.module.scss";
import { DrawToolbarApi } from "./hooks";
import { buildIcon, penIcon, penIconWhite, redoIcon, runIcon, undoIcon } from "./icons";
import { getBallIconFromSport, getToolTipText } from "./utils";

const TOOLTIP_DELAY = 1000;

export type DrawToolbarProps = DrawToolbarApi;

export const DrawToolbar = ({
  freeDraw,
  fastPlace,
  animationView,
  fastBuild,
  giveBall,
  drawColor,
  undo,
  redo,
}: DrawToolbarProps) => {
  const { playStateApi } = usePlayContext();
  const { sport } = playStateApi.playState;

  return (
    <div className={styles.drawToolbar}>
      <Tooltip2 content={getToolTipText("freeDraw")} hoverOpenDelay={TOOLTIP_DELAY}>
        <Button
          id="freeDrawButton"
          className={classNames({ [styles.active || ""]: freeDraw.isActive })}
          onClick={freeDraw.toggle}
        >
          <img className="draw-image" src={freeDraw.isActive ? penIconWhite : penIcon} />
        </Button>
      </Tooltip2>

      <FastAddContextMenu fastPlace={fastPlace}>
        <Tooltip2 content={getToolTipText("sets")} hoverOpenDelay={TOOLTIP_DELAY}>
          <Button id="setsButton">
            <span className={styles.buttonLabel}>{getSetLabelFromSport(sport).toUpperCase()}</span>
          </Button>
        </Tooltip2>
      </FastAddContextMenu>

      <Tooltip2 content={getToolTipText("fastPlace", sport)} hoverOpenDelay={TOOLTIP_DELAY}>
        <Button
          id="fastPlaceButton"
          className={classNames({ [styles.active || ""]: fastPlace.isActive })}
          onClick={fastPlace.toggle}
        >
          FAST PLACE
        </Button>
      </Tooltip2>

      <Tooltip2 content={getToolTipText("fastBuild")} hoverOpenDelay={TOOLTIP_DELAY}>
        <Button id="fastBuildButton" onClick={fastBuild}>
          <img src={buildIcon} />
          <Divider />
          <span className={styles.buttonLabel}>FASTBUILD</span>
        </Button>
      </Tooltip2>

      <Tooltip2 content={getToolTipText("giveBall", sport)} hoverOpenDelay={TOOLTIP_DELAY}>
        <Button id="giveBallButton" onClick={giveBall}>
          <img src={getBallIconFromSport(sport)} />
          <Divider />
          <span className={styles.buttonLabel}>GIVE {getBallLabelFromSport(sport).toUpperCase()}</span>
        </Button>
      </Tooltip2>

      {drawColor.isActive && <SketchPicker color={drawColor.color} onChangeComplete={drawColor.handleChangeColor} />}

      <Tooltip2 content={getToolTipText("undo")} hoverOpenDelay={TOOLTIP_DELAY}>
        <Button id="undoButton" onClick={undo}>
          <img src={undoIcon} />
        </Button>
      </Tooltip2>

      <Tooltip2 content={getToolTipText("redo")} hoverOpenDelay={TOOLTIP_DELAY}>
        <Button id="redoButton" onClick={redo}>
          <img src={redoIcon} />
        </Button>
      </Tooltip2>

      <Tooltip2 content={getToolTipText("animate")} hoverOpenDelay={TOOLTIP_DELAY}>
        <Button
          id="animateButton"
          onClick={(e) => {
            e.currentTarget.blur();
            animationView.toggle();
          }}
        >
          <img src={runIcon} />
          <Divider />
          <span className={styles.buttonLabel}>ANIMATE</span>
        </Button>
      </Tooltip2>
    </div>
  );
};
